import clsx from "clsx";
import { Fragment, useMemo, useState } from "react";
import { FiMessageCircle } from "react-icons/fi";
import { HiOutlineDotsVertical } from "react-icons/hi";
import noImage from "@/assets/img/no-image.svg";
import type { ParsedThreadDetailPost } from "@/entities/post";
import { PlayerTrigger } from "@/features/player/components";
import { DisplayIdMenu, PostActionMenu } from "@/features/thread/ui";
import {
  decoratePostBody,
  formatDate,
  isVideoAttachment,
  type LinkSegment,
  segmentize,
} from "@/shared/lib";
import { VideoPlayOverlay } from "@/shared/ui/media";
import type { ReactionCountsMap } from "../hooks/useReactionCounts";
import { useSoudaneMutation } from "../hooks/useSoudaneMutation";
import type { QuoteReferencesMap } from "../utils/extractQuoteReferences";
import { SoudaneButton } from "./actions/SoudaneButton";
import { LineDisplay } from "./LineDisplay";
import { QuoteSourcesModal } from "./modals/QuoteSourcesModal";
import { OgpCardList } from "./OgpCardList";

interface Props {
  post: ParsedThreadDetailPost;
  /** モーダル等のサブビュー内表示。プレイリスト登録・チェックボックスを無効化 */
  isSubView?: boolean;
  className?: string;
  postNumberClassName?: string;
  onQuoteClick?: (quoteText: string) => void;
  quoteReferencesMap?: QuoteReferencesMap;
  allPosts?: ParsedThreadDetailPost[];
  /** 親スレッドID。新APIではポスト単体に thread_id がないため親から渡す */
  threadId?: string;
  onJumpToPost?: () => void;
  /** ThreadView で 1 回 fetch した反応カウントマップ */
  reactionCounts?: ReactionCountsMap;
}

export const PostDisplay: React.FunctionComponent<Props> = ({
  post,
  isSubView,
  className,
  onQuoteClick,
  quoteReferencesMap,
  allPosts,
  threadId,
  onJumpToPost,
  reactionCounts,
}: Props) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [quoteSourcesOpen, setQuoteSourcesOpen] = useState(false);
  const [displayIdMenuOpen, setDisplayIdMenuOpen] = useState(false);
  const { mutate: soudane, isPending } = useSoudaneMutation();

  // 反応カウントは別エンドポイント (/reactions) で取得され、postNumber (number) で索引される。
  // ParsedThreadDetailPost.postNumber は string なので Number() で変換。
  // 取得失敗時 / レス未反応時は 0 表示にフォールバック。
  const soudaneCount =
    reactionCounts?.get(Number(post.postNumber))?.soudane ?? 0;

  // このレスを引用している元レスのインデックス一覧
  const quoteSourceIndexes: number[] = quoteReferencesMap?.get(post.id) || [];

  // 1レス目のdisplayIdを取得
  const opDisplayId: string | null = allPosts?.[0]?.displayId || null;

  // スレ1レス目の本文に「レインボー」が含まれるかで虹色モード判定。
  const isRainbow = useMemo<boolean>(
    () =>
      allPosts?.[0]?.body.some((line) => line.text.includes("レインボー")) ??
      false,
    [allPosts],
  );

  // 現在のdisplayIdが1レス目と同じかチェック
  const isOpId =
    post.displayId && opDisplayId && post.displayId === opDisplayId;

  // 新APIでは attachments は配列。Task 6 の最小変更として最初の1件のみ表示する。
  // TODO(Task 7+): attachments.map で複数枚に対応する。
  const attachment = post.attachments?.[0] ?? null;

  // サムネイル表示用 (一覧・投稿表示用)。
  // is_animated な画像 (GIF/APNG/Animated WebP 等) はサムネに置き換えず
  // フルサイズで表示する。新APIの url / thumbnailUrl は絶対URL (nullable)。
  const thumbnailUrl = attachment
    ? ((attachment.isAnimated
        ? attachment.url
        : (attachment.thumbnailUrl ?? attachment.url)) ?? noImage)
    : null;
  // フルサイズ表示用（別タブで開く用）
  const fullImageUrl = attachment?.url ?? null;
  const isVideo = attachment ? isVideoAttachment(attachment) : false;
  // 添付ファイル名（旧 attachment.path → 新 url の末尾）
  const attachmentName =
    attachment?.url?.split("/").pop() ?? `${attachment?.id ?? ""}`;

  // OGP表示用のリンクリスト（引用行のリンクは対象外）
  const links = useMemo<string[]>(
    () =>
      post.body
        .filter((line) => line.type !== "quote")
        .flatMap((line) => segmentize(line.text))
        .filter((seg): seg is LinkSegment => seg.type === "link")
        .map((seg) => seg.href),
    [post.body],
  );

  // 表示専用の装飾済 body
  const decoratedBody = decoratePostBody(post.body);

  // オリジナルサイズからサムネ/表示サイズを計算
  const ow = attachment?.width ?? null;
  const oh = attachment?.height ?? null;
  const maxSize = isVideo ? 400 : 250;
  let thumbWidth = maxSize;
  let thumbHeight = maxSize;
  if (ow && oh) {
    const scale = Math.min(maxSize / ow, maxSize / oh, 1);
    thumbWidth = Math.round(ow * scale);
    thumbHeight = Math.round(oh * scale);
  }

  return (
    <article className={className}>
      <header className="flex items-center justify-between gap-2 -mt-1.5 -mb-0.5 text-xs text-muted-foreground">
        <div className="flex items-center flex-wrap gap-2">
          <span className="reply-number-in-thread">{post.postNumber}</span>
          {post.name ? (
            <span className="reply-name px-1.5 py-0.5 bg-accent-400/30 text-accent rounded font-bold text-xs">
              {post.name}
            </span>
          ) : (
            " "
          )}
          <span data-timestamp={post.createdAt}>
            {formatDate(post.createdAt)}
          </span>
          {post.displayId && (
            <label
              className={clsx(
                "display-id hover:underline cursor-pointer",
                isOpId ? "text-accent" : "text-destructive",
              )}
            >
              <button
                type="button"
                onClick={(): void => setDisplayIdMenuOpen(true)}
              />
              ID:{post.displayId}
            </label>
          )}
          <label
            className={clsx(
              "post-no underline hover:text-foreground cursor-pointer",
              attachment?.isOtegaki && "text-otegaki",
            )}
          >
            <button type="button" onClick={(): void => setMenuOpen(true)} />
            No.{post.postNumber}
          </label>
          <SoudaneButton
            count={soudaneCount}
            onClick={(): void =>
              soudane({
                threadId: threadId ?? "",
                postNumber: post.postNumber,
              })
            }
            disabled={isPending}
          />
          {quoteSourceIndexes.length > 0 && (
            <button
              type="button"
              onClick={(): void => setQuoteSourcesOpen(true)}
              className="flex items-center gap-1 px-1.5 py-0.5 rounded hover:bg-muted text-primary transition-colors"
              aria-label={`${quoteSourceIndexes.length}件の引用元を表示`}
            >
              <FiMessageCircle className="w-3.5 h-3.5" />
              <span className="text-xs font-medium">
                {quoteSourceIndexes.length}
              </span>
            </button>
          )}
        </div>
        <button
          type="button"
          onClick={(): void => setMenuOpen(true)}
          className="p-1.5 rounded-full hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
          aria-label="メニュー"
        >
          <HiOutlineDotsVertical className="w-5 h-5" />
        </button>
      </header>

      {thumbnailUrl && attachment && (
        <figure className="ml-2 mb-2 text-xs text-muted-foreground">
          {fullImageUrl && (
            <figcaption>
              {isVideo ? "動画ファイル名:" : "画像ファイル名:"}
              <a
                href={fullImageUrl || thumbnailUrl}
                target="_blank"
                className="mx-1 text-primary hover:text-primary/80 underline"
              >
                {attachmentName}
              </a>
              -({attachment.sizeBytes}B)
              <a
                href={`${fullImageUrl}?original=1`}
                download
                className="ml-1 text-primary hover:text-primary/80 underline"
              >
                [保存]
              </a>
            </figcaption>
          )}
          <a
            href={fullImageUrl || thumbnailUrl}
            target="_blank"
            className="relative inline-block"
          >
            <img
              src={thumbnailUrl}
              alt={fullImageUrl ?? ""}
              className="max-w-full rounded-lg"
              width={thumbWidth}
              height={thumbHeight}
            />
            {isVideo && <VideoPlayOverlay />}
          </a>
          {fullImageUrl && (
            <div className="mt-1">
              {isVideo && (
                <PlayerTrigger
                  url={fullImageUrl}
                  threadId={threadId ?? ""}
                  postNo={post.postNumber}
                  isSubView={isSubView}
                />
              )}
            </div>
          )}
        </figure>
      )}

      {/* 本文表示 */}
      <blockquote className="ml-2 text-foreground leading-relaxed">
        {post.email && <p className="text-act font-bold">{post.email}</p>}
        {decoratedBody.map((line, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: decoratedBody は同 post 内で順序・件数が固定のため index は安定キー
          <Fragment key={i}>
            {i > 0 && <br />}
            <LineDisplay
              line={line}
              onQuoteClick={onQuoteClick}
              threadId={threadId ?? ""}
              postNumber={post.postNumber}
              isSubView={isSubView}
              isRainbow={isRainbow}
            />
          </Fragment>
        ))}
      </blockquote>

      {/* OGPカード表示 */}
      {links.length > 0 && <OgpCardList urls={links} />}

      <PostActionMenu
        isOpen={menuOpen}
        onClose={(): void => setMenuOpen(false)}
        post={post}
        threadId={threadId ?? ""}
        onJumpToPost={onJumpToPost}
      />

      {/* 引用元一覧モーダル */}
      {allPosts && quoteSourceIndexes.length > 0 && (
        <QuoteSourcesModal
          isOpen={quoteSourcesOpen}
          onClose={(): void => setQuoteSourcesOpen(false)}
          sourceIndexes={quoteSourceIndexes}
          posts={allPosts}
          threadId={threadId ?? ""}
          quoteReferencesMap={quoteReferencesMap}
          onQuoteClick={onQuoteClick}
          onJumpToPost={onJumpToPost}
        />
      )}

      {/* Display IDメニュー */}
      {post.displayId && (
        <DisplayIdMenu
          isOpen={displayIdMenuOpen}
          onClose={(): void => setDisplayIdMenuOpen(false)}
          displayId={post.displayId}
          threadId={threadId ?? ""}
          allPosts={allPosts}
          quoteReferencesMap={quoteReferencesMap}
          onQuoteClick={onQuoteClick}
        />
      )}
    </article>
  );
};
